import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, MapPin, Mail, ShieldCheck } from "lucide-react";

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_d04ef3c1-d7de-40d8-b6b3-0d99244d2801/artifacts/0hpd8r65_icon_markremoved%20backround.png";

const FOOTER_COMPANY = [
  { label: "Company", to: "/company" },
  { label: "Blog", to: "/blog" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
  { label: "Status", to: "/system-status" },
];

const FOOTER_LEGAL = [
  { label: "Legal", to: "/legal" },
  { label: "Privacy", to: "/privacy" },
  { label: "Terms", to: "/terms" },
  { label: "Security", to: "/security" },
];

const NORWEGIAN_INFO = [
  { icon: Building2, title: "Company", lines: ["Global InTech AS", "Org. No: NO 935 706 998"] },
  { icon: MapPin, title: "Registered Address", lines: ["R\u00f8o 95", "5457 H\u00f8ylandsbygd, Norway"] },
  { icon: Mail, title: "Contact", lines: ["contact@qnneal.dev"], mailto: "contact@qnneal.dev" },
  { icon: ShieldCheck, title: "Data Protection", lines: ["privacy@qnneal.dev"], mailto: "privacy@qnneal.dev" },
];

export default function SiteFooter() {
  return (
    <footer className="border-t border-[var(--qn-border)] bg-[var(--qn-background)]" data-testid="site-footer" data-automation-id="site-footer">
      {/* Upper section: QNNEAL branding + nav columns */}
      <div className="max-w-7xl mx-auto px-6 pt-12 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <motion.img src={LOGO_ICON} alt="QNNEAL" className="h-10 w-10 logo-spin" whileHover={{ scale: 1.05 }} />
              <span className="font-outfit font-bold text-xl">QNNEAL</span>
            </div>
            <p className="text-[var(--qn-foreground-muted)] text-sm leading-relaxed max-w-sm">
              The quantum-enhanced agentic AI platform that optimizes your multi-provider AI workflows for cost, latency, and performance.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-sm mb-4 text-[var(--qn-foreground)]">Company</h4>
            <ul className="space-y-2.5">
              {FOOTER_COMPANY.map(l => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-[var(--qn-foreground-muted)] hover:text-[var(--qn-primary)] transition-colors" data-testid={`footer-link-${l.to.slice(1)}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-sm mb-4 text-[var(--qn-foreground)]">Legal</h4>
            <ul className="space-y-2.5">
              {FOOTER_LEGAL.map(l => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-[var(--qn-foreground-muted)] hover:text-[var(--qn-primary)] transition-colors" data-testid={`footer-link-${l.to.slice(1)}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Norwegian mandatory company information */}
      <div className="border-t border-[var(--qn-border)]">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="norwegian-company-info" data-automation-id="norwegian-company-info">
            {NORWEGIAN_INFO.map((item, i) => (
              <div key={i} className="flex gap-3">
                <item.icon className="w-5 h-5 text-[var(--qn-primary)] shrink-0 mt-0.5" />
                <div>
                  <h5 className="text-sm font-semibold text-[var(--qn-foreground)] mb-1.5">{item.title}</h5>
                  {item.lines.map((line, j) => (
                    item.mailto ? (
                      <a key={j} href={`mailto:${item.mailto}`} className="block text-xs text-[var(--qn-foreground-muted)] hover:text-[var(--qn-primary)] transition-colors leading-relaxed" data-testid={`footer-info-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                        {line}
                      </a>
                    ) : (
                      <p key={j} className="text-xs text-[var(--qn-foreground-muted)] leading-relaxed">{line}</p>
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-[var(--qn-border)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-[var(--qn-foreground-subtle)]" data-testid="footer-copyright" data-automation-id="footer-copyright">
              &copy; 2026 Global InTech AS. All rights reserved.
            </p>
            <div className="flex items-center gap-4" data-testid="footer-social-links" data-automation-id="footer-social-links">
              <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="text-[var(--qn-foreground-subtle)] hover:text-[var(--qn-foreground)] transition-colors" aria-label="X (Twitter)">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-[var(--qn-foreground-subtle)] hover:text-[var(--qn-foreground)] transition-colors" aria-label="LinkedIn">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="text-[var(--qn-foreground-subtle)] hover:text-[var(--qn-foreground)] transition-colors" aria-label="GitHub">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
